import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { setupAPIclient } from '../../services/setupAPIclient';
import LocalStorage from "../../helpers/localstorage";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiTrash2, FiSave, FiPlusCircle, FiCheckCircle, FiSearch, 
  FiBox, FiHash, FiEdit, FiLayers 
} from 'react-icons/fi';

interface FormUnit {
  id: number;
  name: string;
  regionName: string;
  region_id: number;
}

interface FormRegionInput {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function UnitForm() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const [data, setData] = useState<FormUnit[]>([]);
  const [filterText, setFilterText] = useState("");
  const [pending, setPending] = useState(true);
  const [dataRegion, setDataRegion] = useState<FormRegionInput[]>([]);
  const [formUnit, setFormUnit] = useState({ name: "", region_id: 0 });

  const ApiClient = setupAPIclient(token);

  const handleGetDataInput = async () => {
    try {
      const response = await ApiClient.post<{ success: boolean; data: any[] }>('/management/get/unit');
      const allUnits: FormUnit[] = response.data.flatMap(region =>
        region.management_unit.map((unit: any) => ({
          ...unit,
          regionName: region.name,
          region_id: region.id
        }))
      );
      setData(allUnits);
      setPending(false);
    } catch (error) {
      console.error("Erro ao buscar unidades:", error);
      toast.error("Erro ao carregar unidades");
    }
  };

  const handleGetDataRegion = async () => {
    try {
      const response = await ApiClient.post<{ success: boolean; data: FormRegionInput[] }>('/management/get/region');
      setDataRegion(response.data);
    } catch (error) {
      console.error("Erro ao buscar regiões:", error);
      toast.error("Erro ao carregar regiões");
    }
  };

  const handleUpdate = async (row: FormUnit) => {
    try {
      await ApiClient.post('/management/update/unit', {
        id: row.id,
        name: row.name,
        region_id: row.region_id
      });
      toast.success("Registro atualizado com sucesso");
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao atualizar:", error);
      toast.error("Erro ao atualizar registro");
    }
  };

  const handleCreate = async () => {
    if (!formUnit.name || !formUnit.region_id) {
      toast.warn("Preencha todos os campos antes de cadastrar");
      return;
    }
    try {
      await ApiClient.post('/management/create/unit', { ...formUnit });
      toast.success("Unidade cadastrada com sucesso");
      setFormUnit({ name: "", region_id: 0 });
      handleGetDataInput();
    } catch (error) {
      console.error("Erro ao cadastrar:", error);
      toast.error("Erro ao cadastrar unidade");
    }
  };

  const handleDelete = async (row: FormUnit) => {
    if (!window.confirm(`Tem certeza que deseja deletar a unidade ${row.name}?`)) return;
    try {
      const response = await ApiClient.post('/management/delete/unit', { id: row.id });
      if (!response.data.success) {
        toast.warning(`Não é possível deletar. Existem dados associados à unidade ${row.name}`);
      } else {
        toast.success("Registro deletado com sucesso");
        handleGetDataInput();
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
      toast.warning(`Não é possível deletar. Existem dados associados à unidade ${row.name}`);
    }
  };

  useEffect(() => {
    handleGetDataInput();
    handleGetDataRegion();
  }, []);

  const iconColor = "#5dade2"; // COR DOS ÍCONES
  const buttonColor = "#e74c3c"; // COR DOS BOTÕES
  const iconSize = 20;

  const columns: TableColumn<FormUnit>[] = [
    {
      name: <div className="d-flex align-items-center gap-1"><FiHash color={iconColor} size={iconSize} /> ID</div>,
      selector: row => row.id,
      sortable: true,
      minWidth: "80px",
      maxWidth: "120px"
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiLayers color={iconColor} size={iconSize} /> Região</div>,
      selector: row => row.regionName,
      sortable: true,
      cell: (row) => (
        <select
          className="form-control form-control-sm"
          value={row.region_id}
          onChange={(e) =>
            setData(prev => prev.map(r => r.id === row.id ? { ...r, region_id: parseInt(e.target.value) } : r))
          }
        >
          <option value={0}>Selecione a região</option>
          {dataRegion.map(region => (
            <option key={region.id} value={region.id}>{region.name}</option>
          ))}
        </select>
      ),
      minWidth: "120px"
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiBox color={iconColor} size={iconSize} /> Unidade</div>,
      selector: row => row.name,
      sortable: true,
      cell: (row) => (
        <input
          type="text"
          className="form-control form-control-sm"
          value={row.name ?? ""}
          onChange={(e) =>
            setData(prev => prev.map(r => r.id === row.id ? { ...r, name: e.target.value } : r))
          }
        />
      ),
      minWidth: "150px"
    },
    {
      name: <div className="d-flex align-items-center gap-1"><FiEdit color={iconColor} size={iconSize} /> Ações</div>,
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <motion.button 
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            className="btn btn-md d-flex align-items-center gap-1"
            style={{ borderColor: buttonColor, color: buttonColor }}
            onClick={() => handleUpdate(row)}
          >
            <FiSave color={iconColor} /> 
          </motion.button>
          <motion.button 
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            className="btn btn-md d-flex align-items-center gap-1"
            style={{ borderColor: buttonColor, color: buttonColor }}
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 color={iconColor} /> 
          </motion.button>
        </div>
      ),
      center: true,
      minWidth: "180px"
    }
  ];

  const filteredItems = data.filter(item =>
    item.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <Layout>
      {/* Título e cards */}
      <div className="mb-4">
        <h4 className="d-flex align-items-center gap-2">
          <FiBox color={iconColor} size={26} /> Configuração de Unidades
        </h4>
        <div className="d-flex gap-3 mt-3 flex-wrap">
          <div className="card flex-fill p-3 shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <FiLayers color={iconColor} size={22} />
              <div>
                <div>Total de Regiões</div>
                <strong>{dataRegion.length}</strong>
              </div>
            </div>
          </div>
          <div className="card flex-fill p-3 shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <FiBox color={iconColor} size={22} />
              <div>
                <div>Total de Unidades</div>
                <strong>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formulário de criação */}
      <motion.div className="card p-4 mb-4 shadow-sm" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <FiPlusCircle color={iconColor} size={iconSize} /> Nova Unidade
        </h5>
        <div className="row align-items-end g-3">
          <div className="col-md-4">
            <label className="form-label">Nome da Unidade</label>
            <input 
              type="text" 
              className="form-control" 
              placeholder="Digite o nome da unidade" 
              value={formUnit.name} 
              onChange={(e) => setFormUnit({ ...formUnit, name: e.target.value })} 
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Região</label>
            <select 
              className="form-control" 
              value={formUnit.region_id} 
              onChange={(e) => setFormUnit({ ...formUnit, region_id: parseInt(e.target.value) })}
            >
              <option value={0}>Selecione a região</option>
              {dataRegion.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2 d-grid">
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }} 
              className="btn d-flex align-items-center justify-content-center gap-1"
              style={{ borderColor: buttonColor, color: buttonColor }}
              onClick={handleCreate}
            >
              <FiCheckCircle color={iconColor} /> Cadastrar
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Pesquisa */}
      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-white"><FiSearch color={iconColor} /></span>
          <input 
            type="text" 
            className="form-control" 
            placeholder="Pesquisar unidade..." 
            value={filterText} 
            onChange={(e) => setFilterText(e.target.value)} 
          />
        </div>
      </div>

      {/* Tabela */}
      <DataTable
        columns={columns}
        data={filteredItems}
        progressPending={pending}
        pagination
        highlightOnHover
        pointerOnHover
        responsive
        striped
        dense
        noHeader
      />
    </Layout>
  );
}

export default UnitForm;
